import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import sessionsService from '../../services/sessions';

export type DuplicatedSession = {
  sourceSessionId: number;
  sessionId: number;
};

interface Props {
  open: boolean;
  sessionIds: string[];
  onClose: () => void;
  onDuplicated?: (duplicates: DuplicatedSession[]) => void;
}

export const sessionLink = (id: number | string) => '/session/prepare/' + id;

const failureMessage = (e: any) => {
  const failures = e?.response?.data?.failures?.map((x: { message: string }) => x.message?.trim()).filter((x: string) => x);
  if (failures && failures.length > 0) {
    return failures.join(' | ');
  }
  return e?.response?.status ? `HTTP ${e.response.status}` : e?.message;
};

const SessionDuplicateDialog: React.FunctionComponent<Props> = ({ open, sessionIds, onClose, onDuplicated }) => {
  const [label, setLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const count = sessionIds.length;
  const canDuplicate = !loading && count > 0 && label.trim() !== '';

  useEffect(() => {
    if (open) {
      setLabel('');
    }
  }, [open]);

  const duplicate = useCallback(async () => {
    setLoading(true);
    try {
      const trimmed = label.trim();
      let duplicates: DuplicatedSession[];
      if (count === 1) {
        duplicates = [await sessionsService.duplicateSession(sessionIds[0], trimmed)];
      } else {
        const response = await sessionsService.duplicateSessions(sessionIds.map(Number), trimmed);
        duplicates = response?.items || [];
      }

      enqueueSnackbar(
        <span>
          {duplicates.length === 1 ? 'Created duplicate session ' : 'Created duplicate sessions '}
          {duplicates.map((d, i) => (
            <React.Fragment key={d.sessionId}>
              {i > 0 && ', '}
              <Link to={sessionLink(d.sessionId)} style={{ color: 'inherit', textDecoration: 'underline' }}>
                #{d.sessionId}
              </Link>
              {count > 1 && ` (of #${d.sourceSessionId})`}
            </React.Fragment>
          ))}
        </span>,
        { variant: 'success', autoHideDuration: 15000 }
      );
      onDuplicated && onDuplicated(duplicates);
      onClose();
    } catch (e) {
      enqueueSnackbar('Failed to duplicate: ' + failureMessage(e), { variant: 'error', autoHideDuration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [count, label, sessionIds, enqueueSnackbar, onDuplicated, onClose]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} aria-labelledby="duplicate-session-title">
      <DialogTitle id="duplicate-session-title">
        {count === 1 ? `Duplicate session #${sessionIds[0]}` : `Duplicate ${count} sessions`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Creates {count === 1 ? 'a new session' : 'new sessions'} with the same videos, periods, calibrations, pitch,
          teams, line-ups and events. Runs, label tasks and exports are not copied; the original stays untouched.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          required
          label="Label"
          helperText="Added to the session label, e.g. csharp-shadow"
          value={label}
          inputProps={{ maxLength: 100 }}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canDuplicate) {
              duplicate();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={duplicate} color="primary" variant="contained" disabled={!canDuplicate}>
          {loading ? <CircularProgress size={24} /> : 'Duplicate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionDuplicateDialog;
